export interface TraceNode {
    id: string
    name: string
    type: 'chain' | 'tool' | 'llm' | 'retriever' | 'unknown'
    status: 'success' | 'error' | 'running' | 'warning'
    startTime: number
    endTime?: number
    latency?: number
    inputs?: any
    outputs?: any
    metadata?: any
    error?: string
    children: TraceNode[]
    depth: number
}

// Helper to normalize the raw backend response into a clean TraceNode tree
export function parseTraceData(rawData: any): TraceNode | null {
    if (!rawData) return null

    // Helper to calculate latency safely
    const getLatency = (start: number, end?: number) => {
        if (!start || !end) return undefined
        return end - start
    }

    // Helper to determine status color/type
    const getStatus = (node: any): TraceNode['status'] => {
        if (node.error) return 'error'
        if (node.status === 'error' || node.status === 'failed') return 'error'
        if (node.status === 'refused') return 'warning' // RAG specific
        if (!node.end_time) return 'running'
        return 'success'
    }

    // Recursive transformation function
    const transform = (node: any, depth: number = 0): TraceNode => {
        const type = (node.type || 'unknown').toLowerCase()

        // Map backend fields to frontend interface
        // Handling both snake_case (Python) and potential camelCase if changed
        const startTime = new Date(node.start_time || node.timestamp).getTime()
        const endTime = node.end_time ? new Date(node.end_time).getTime() : undefined

        return {
            id: node.id || node.trace_id || crypto.randomUUID(),
            name: node.name || node.k || 'Unnamed Step',
            type: inferType(node.name, type),
            status: getStatus(node),
            startTime,
            endTime,
            latency: node.latency_ms || getLatency(startTime, endTime),
            inputs: node.inputs || node.args,
            outputs: node.outputs || node.result,
            metadata: node.metadata || node.extra,
            error: node.error_message || node.error,
            depth,
            children: (node.children || []).map((child: any) => transform(child, depth + 1))
        }
    }

    return transform(rawData)
}

function inferType(name: string, rawType: string): TraceNode['type'] {
    const lowerName = (name || '').toLowerCase()
    if (rawType.includes('llm') || lowerName.includes('gpt') || lowerName.includes('generate')) return 'llm'
    if (rawType.includes('retriev') || lowerName.includes('search') || lowerName.includes('vector')) return 'retriever'
    if (rawType.includes('tool') || lowerName.includes('tool')) return 'tool'
    return 'chain' // Default container
}
